import { MouseEvent, ReactNode } from "react";
import { Box, Chip, LinearProgress, Stack, Typography } from "@mui/material";
import moment from "moment";
import { Link } from "react-router-dom";
import {
  EvidenceView,
  GovItem,
  GovSettingsOn,
  Party,
  ReleaseRef,
} from "../../../../utils/icGovernance";
import { PRIMARY, itemUrl, itemUrlById, partyLabel } from "../governanceStatus";

/**
 * Small cell renderers shared by the five registers (`registers/*Register.tsx`)
 * and the weekly status list. Every link inside a table row stops the click so
 * the row's own "open the item" handler does not fire as well.
 */

export const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace";
export const MUTED = "#8a8a93";
export const INK = "#2c2c34";

const stop = (e: MouseEvent) => e.stopPropagation();

/** A timestamp (createdAt, decidedAt, closedAt…) as the viewer's local day. */
export const localDay = (iso: string | null | undefined): string =>
  iso ? moment(iso).format("YYYY-MM-DD") : "—";

export const Muted = ({ children = "—" }: { children?: ReactNode }) => (
  <Box component="span" sx={{ color: MUTED }}>
    {children}
  </Box>
);

/** Plain text or a muted dash. */
export const PlainText = ({ value }: { value: string | number | null | undefined }) =>
  value === null || value === undefined || value === "" ? <Muted /> : <>{String(value)}</>;

/** Display ID in monospace bold, linking to the item. */
export const IdLink = ({ item }: { item: Pick<GovItem, "id" | "displayId"> }) => (
  <Link
    to={itemUrl(item)}
    onClick={stop}
    style={{ fontFamily: MONO, fontWeight: 700, color: PRIMARY, textDecoration: "none", whiteSpace: "nowrap" }}
  >
    {item.displayId || "Open"}
  </Link>
);

export const TitleLink = ({ item }: { item: Pick<GovItem, "id" | "displayId" | "title"> }) => (
  <Link to={itemUrl(item)} onClick={stop} style={{ color: INK, fontWeight: 600, textDecoration: "none" }}>
    {item.title || "(untitled)"}
  </Link>
);

/** Long free text: keeps line breaks, clamped to a few lines (full text on hover). */
export const LongText = ({ value, lines = 4 }: { value: string | null | undefined; lines?: number }) => {
  if (!value || !value.trim()) return <Muted />;
  return (
    <Box
      component="span"
      title={value}
      sx={{
        display: "-webkit-box",
        WebkitLineClamp: lines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        whiteSpace: "pre-line",
      }}
    >
      {value}
    </Box>
  );
};

/** Linked display IDs as monospace links, comma separated. */
export const LinkedIdList = ({ ids }: { ids: string[] }) => {
  if (ids.length === 0) return <Muted />;
  return (
    <Box component="span" sx={{ whiteSpace: "normal" }}>
      {ids.map((id, i) => (
        <span key={`${id}-${i}`}>
          {i > 0 ? ", " : null}
          <Link
            to={itemUrlById(id)}
            onClick={stop}
            style={{ fontFamily: MONO, color: PRIMARY, textDecoration: "none", whiteSpace: "nowrap" }}
          >
            {id}
          </Link>
        </span>
      ))}
    </Box>
  );
};

/** Only http(s) evidence becomes a link; anything else is shown as text. */
const isWebUrl = (url: string) => /^https?:\/\//i.test(url || "");

export const EvidenceLinks = ({ evidence }: { evidence: EvidenceView[] | null | undefined }) => {
  const list = evidence ?? [];
  if (list.length === 0) return <Muted />;
  return (
    <Box component="span" sx={{ whiteSpace: "normal" }}>
      {list.map((e, i) => (
        <span key={`${e.url}-${i}`}>
          {i > 0 ? ", " : null}
          {isWebUrl(e.url) ? (
            <a
              href={e.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={stop}
              style={{ color: PRIMARY }}
            >
              {e.label || "Link"}
            </a>
          ) : (
            <span>{e.label || e.url}</span>
          )}
        </span>
      ))}
    </Box>
  );
};

/** A release reference as "{displayId} v{version}", linking to the release. */
export const ReleaseLink = ({ release }: { release: ReleaseRef | null | undefined }) => {
  if (!release) return <Muted />;
  const label = release.displayId || release.title || "Release";
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <Link
        to={itemUrlById(release.displayId || release.id)}
        onClick={stop}
        style={{ fontFamily: MONO, color: PRIMARY, textDecoration: "none" }}
      >
        {label}
      </Link>
      {release.version ? <Box component="span" sx={{ color: MUTED }}> v{release.version}</Box> : null}
    </span>
  );
};

/** % complete as a small bar plus the number. */
export const PercentBar = ({ value }: { value: number | null | undefined }) => {
  const pct = typeof value === "number" ? Math.max(0, Math.min(100, Math.round(value))) : null;
  if (pct === null) return <Muted />;
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120 }}>
      <LinearProgress
        variant="determinate"
        value={pct}
        aria-label={`${pct}% complete`}
        sx={{
          width: 80,
          height: 8,
          borderRadius: 4,
          backgroundColor: "#e5e7eb",
          "& .MuiLinearProgress-bar": { backgroundColor: pct >= 100 ? "#16a34a" : PRIMARY, borderRadius: 4 },
        }}
      />
      <Typography variant="body2" component="span">
        {pct}%
      </Typography>
    </Stack>
  );
};

/** Signed hours variance; over the estimate in amber, under in green. */
export const VarianceText = ({ text }: { text: string }) => {
  const color = text.startsWith("+") ? "#b45309" : text.startsWith("−") ? "#1e7b3c" : undefined;
  return (
    <Box component="span" sx={{ color, fontVariantNumeric: "tabular-nums" }}>
      {text}
    </Box>
  );
};

export const PartyChip = ({ party, settings }: { party: Party | null | undefined; settings: GovSettingsOn }) => {
  if (!party) return <Muted />;
  const client = party === "CLIENT";
  return (
    <Chip
      size="small"
      label={partyLabel(party, settings)}
      sx={{
        fontWeight: 600,
        backgroundColor: client ? "#e8f0fe" : "#f0f0f3",
        color: client ? "#1a56c4" : "#5a5a66",
      }}
    />
  );
};
